import React, { FunctionComponent, useState } from "react";
import { makeStyles } from "tss-react/mui";
import moment from "moment/moment";
import {
    AppBar,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    Theme,
    Toolbar,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css";
import CheckIcon from "@mui/icons-material/Check";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { de } from "date-fns/locale";
import { CHANGE_DATES_START } from "@/Redux/Disposition/Constants"; // theme css file
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { useAppDispatch, useAppSelector } from "@/Redux/Redux";
import {
    ChevronDoubleLeft,
    ChevronDoubleRight,
    ChevronDown,
    ChevronRight,
} from "mdi-material-ui";
import { isMobile, isTablet } from "react-device-detect";
import {useSelector} from "react-redux";
import {selectDispoDaysAfter, selectDispoDaysBefore, ViewerState} from "@/Redux/Auth/AuthReducer";
import {useTranslation} from "@/Utils/CustomHooks/useTranslation";

interface DateControlsProps {}

const useStyles = makeStyles()((theme: Theme) => ({
    dispositionControls: {
        flex: "1 1",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        // @ts-ignore
        backgroundColor: theme.palette.background.darker,
    },

    dispositionControlsLeft: {
        flex: "1 1",
        paddingLeft: 15,
    },

    dispoBtn: {
        whiteSpace: "nowrap",
        alignSelf: "center",
        fontSize: ".75rem",
        fontWeight: 700,
        textTransform: "uppercase",
        display: "inline-block",
        cursor: "pointer",
    },

    dispoBtnLeft: {
        marginRight: 5,
    },

    dispoBtnRight: {
        marginLeft: 5,
    },

    dispositionControlsDatepicker: {
        flex: "1 1",
        textAlign: "center",
    },

    dispositionControlsRight: {
        flex: "1 1",
        justifyContent: "flex-end",
        paddingRight: 15,
        textAlign: "right",
    },

    datepickerText: {
        fontSize: ".875rem",
        display: "inline",
    },

    datepickerInputContainer: {
        display: "inline-block",
    },

    datepickerInput: {
        display: "inline-block",
        fontSize: ".875rem",
        fontWeight: 700,
        cursor: "pointer",
        transition: "color .3s ease",
    },

    dispoBtnLink: {
        "&.MuiButton-root": {
            color: theme.palette.text.primary,
        },
        transition: "color .3s ease",
        textDecoration: "none",
        backgroundColor: "transparent",
    },

    submit: {},

    buttonProgress: {
        position: "absolute",
        top: "54%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },

    wrapper: {
        marginTop: theme.spacing(2),
        position: "relative",
    },

    appBar: {
        position: "relative",
        boxShadow: "none",
    },

    title: {
        flex: 1,
    },

    toolbar: {
        height: 94,
    },

    calendar: {
        "&": {
            marginLeft: -10,
            marginRight: -10,
            background: "transparent",
            color: theme.palette.text.primary,
        },
        "& .rdrMonthAndYearWrapper": {
            "& .rdrNextPrevButton": {
                // @ts-ignore
                background: theme.palette.background.darker,
            },
            "& .rdrPprevButton": {
                // @ts-ignore
                "& i": {
                    borderColor:
                        "transparent " +
                        theme.palette.text.primary +
                        " transparent transparent",
                },
            },
            "& .rdrNextButton": {
                "& i": {
                    borderColor:
                        "transparent transparent transparent " +
                        theme.palette.text.primary,
                },
            },
        },
        "& .rdrDayNumber span": {
            color: theme.palette.text.primary,
        },
        "& .rdrDayPassive span": {
            opacity: "0.75",
        },
    },
}));

const DispositionDateControls: FunctionComponent<
    DateControlsProps
> = (): JSX.Element => {
    const { classes, cx } = useStyles();
    const { initialized, dispositionStartdate, dispositionEnddate } =
        useAppSelector((state) => state.disposition);

    const [tmpStartdate, setTmpStartdate] = useState<string | null>(null);
    const [tmpEnddate, setTmpEnddate] = useState<string | null>(null);
    const { t } = useTranslation();

    const dispoDaysBefore = useSelector((state) =>
        selectDispoDaysBefore(state as ViewerState)
    );

    const dispoDaysAfter = useSelector((state) =>
        selectDispoDaysAfter(state as ViewerState)
    );

    const thisWeekStartdate = moment()
        .utc()
        .subtract(dispoDaysBefore, "days")
        .hours(0)
        .minutes(0)
        .seconds(0)
        .milliseconds(0);
    const thisWeekEnddate = moment()
        .utc()
        .add(dispoDaysAfter, "days")
        .hours(0)
        .minutes(0)
        .seconds(0)
        .milliseconds(0);

    if (tmpStartdate === null && tmpEnddate === null && initialized) {
        setTmpStartdate(dispositionStartdate);
        setTmpEnddate(dispositionEnddate);
    }

    const dispatch = useAppDispatch();

    const [open, setOpen] = useState(false);

    const handleClickOpen = () => {
        setOpen(true);
    };
    const handleClose = () => {
        setOpen(false);
    };

    const handleDatePickerChange = (ranges: any) => {
        setTmpStartdate(ranges.selection.startDate);
        setTmpEnddate(ranges.selection.endDate);
    };

    const handleLastMothClick = () => {
        const startdate = moment
            .utc(tmpStartdate)
            .subtract(1, "months")
            .startOf("month");
        const enddate = moment
            .utc(tmpStartdate)
            .subtract(1, "months")
            .endOf("month");
        setTmpStartdate(startdate.format());
        setTmpEnddate(enddate.format());

        dispatch({
            type: CHANGE_DATES_START,
            payload: {
                dispositionStartdate: startdate.format(),
                dispositionEnddate: enddate.format(),
            },
        });
    };

    const handleLastWeekClick = () => {
        const startdate = moment
            .utc(tmpStartdate)
            .subtract(1, "week");
        const enddate = moment
            .utc(tmpEnddate)
            .subtract(1, "week");
        setTmpStartdate(startdate.format());
        setTmpEnddate(enddate.format());

        dispatch({
            type: CHANGE_DATES_START,
            payload: {
                dispositionStartdate: startdate.format(),
                dispositionEnddate: enddate.format(),
            },
        });
    };

    const handleActualWeekClick = () => {
        const startdate = thisWeekStartdate;
        const enddate = thisWeekEnddate;
        setTmpStartdate(startdate.format());
        setTmpEnddate(enddate.format());

        dispatch({
            type: CHANGE_DATES_START,
            payload: {
                dispositionStartdate: startdate.format(),
                dispositionEnddate: enddate.format(),
            },
        });
    };

    const handleNextWeekClick = () => {
        const startdate = moment
            .utc(tmpStartdate)
            .add(1, "week")
        const enddate = moment.utc(tmpEnddate).add(1, "week");
        setTmpStartdate(startdate.format());
        setTmpEnddate(enddate.format());

        dispatch({
            type: CHANGE_DATES_START,
            payload: {
                dispositionStartdate: startdate.format(),
                dispositionEnddate: enddate.format(),
            },
        });
    };

    const handleNextMonthClick = () => {
        const startdate = moment
            .utc(tmpStartdate)
            .add(1, "month")
            .startOf("month");
        const enddate = moment.utc(tmpStartdate).add(1, "month").endOf("month");
        setTmpStartdate(startdate.format());
        setTmpEnddate(enddate.format());

        dispatch({
            type: CHANGE_DATES_START,
            payload: {
                dispositionStartdate: startdate.format(),
                dispositionEnddate: enddate.format(),
            },
        });
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
        <>
            <div className={classes.dispositionControls}>
                <div className={classes.dispositionControlsLeft}>
                    <div className={cx(classes.dispoBtn, classes.dispoBtnLeft)}>
                        {!isMobile && !isTablet ? (
                            <>
                                <Button
                                    className={classes.dispoBtnLink}
                                    onClick={handleLastMothClick}
                                    startIcon={<ChevronDoubleLeft />}
                                    size="small"
                                >
                                    {t("disposition.controls.removeMonth")}
                                </Button>
                            </>
                        ) : (
                            <>
                                <IconButton
                                    size="small"
                                    onClick={handleLastMothClick}
                                >
                                    <ChevronDoubleLeft />
                                </IconButton>
                            </>
                        )}
                    </div>{" "}
                    <div className={cx(classes.dispoBtn, classes.dispoBtnLeft)}>
                        {!isMobile && !isTablet ? (
                            <>
                                <Button
                                    className={classes.dispoBtnLink}
                                    onClick={handleLastWeekClick}
                                    startIcon={<ChevronLeftIcon />}
                                    size="small"
                                >
                                    {t("disposition.controls.removeWeek")}
                                </Button>
                            </>
                        ) : (
                            <>
                                <IconButton
                                    size="small"
                                    onClick={handleLastWeekClick}
                                >
                                    <ChevronLeftIcon />
                                </IconButton>
                            </>
                        )}
                    </div>{" "}
                    <div className={cx(classes.dispoBtn, classes.dispoBtnLeft)}>
                        {!isMobile && !isTablet ? (
                            <>
                                <Button
                                    className={classes.dispoBtnLink}
                                    onClick={handleActualWeekClick}
                                    startIcon={<ChevronDown />}
                                    size="small"
                                >
                                    {t("disposition.controls.actualWeek")}
                                </Button>
                            </>
                        ) : (
                            <>
                                <IconButton
                                    size="small"
                                    onClick={handleActualWeekClick}
                                >
                                    <ChevronDown />
                                </IconButton>
                            </>
                        )}
                    </div>
                </div>
                <div
                    className={classes.dispositionControlsDatepicker}
                    onClick={() => {}}
                >
                    <div
                        id="datepicker"
                        className="vl-calendar-month-dispo"
                        onClick={handleClickOpen}
                    >
                        <div className={classes.datepickerText}>
                            {t("disposition.controls.periodLabel")}&nbsp;
                        </div>
                        <div className={classes.datepickerInputContainer}>
                            <div id="start" className={classes.datepickerInput}>
                                {moment.utc(dispositionStartdate).format("L")}
                            </div>
                            <div className={classes.datepickerText}>
                                &nbsp;–&nbsp;
                            </div>
                            <div className={classes.datepickerInput}>
                                {moment.utc(dispositionEnddate).format("L")}
                            </div>
                            <div className={classes.datepickerText}>
                                <span className="fal fa-chevron-down fa-fw" />
                            </div>
                        </div>
                    </div>
                </div>
                <div className={classes.dispositionControlsRight}>
                    <div
                        className={cx(classes.dispoBtn, classes.dispoBtnRight)}
                    >
                        {!isMobile && !isTablet ? (
                            <>
                                <Button
                                    className={classes.dispoBtnLink}
                                    onClick={handleNextWeekClick}
                                    endIcon={<ChevronRight />}
                                    size="small"
                                >
                                    {t("disposition.controls.addWeek")}
                                </Button>
                            </>
                        ) : (
                            <>
                                <IconButton
                                    size="small"
                                    onClick={handleNextWeekClick}
                                >
                                    <ChevronRight />
                                </IconButton>
                            </>
                        )}
                    </div>{" "}
                    <div
                        className={cx(classes.dispoBtn, classes.dispoBtnRight)}
                    >
                        {!isMobile && !isTablet ? (
                            <>
                                <Button
                                    className={classes.dispoBtnLink}
                                    onClick={handleNextMonthClick}
                                    endIcon={<ChevronDoubleRight />}
                                    size="small"
                                >
                                    {t("disposition.controls.addMonth")}
                                </Button>
                            </>
                        ) : (
                            <>
                                <IconButton
                                    size="small"
                                    onClick={handleNextMonthClick}
                                >
                                    <ChevronDoubleRight />
                                </IconButton>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <Dialog
                onClose={handleClose}
                aria-labelledby="customized-dialog-title"
                open={open}
                maxWidth={"lg"}
            >
                <AppBar className={classes.appBar}>
                    <Toolbar className={classes.toolbar}>
                        <Typography variant="h6" className={classes.title}>
                            {t("disposition.controls.modal.headline")}
                        </Typography>
                        <IconButton aria-label="close" onClick={handleClose}>
                            <CloseIcon />
                        </IconButton>
                    </Toolbar>
                </AppBar>
                <DialogActions style={{ justifyContent: "flex-start" }}>
                    <div className={classes.wrapper}>
                        <Button
                            startIcon={<CheckIcon />}
                            size={"small"}
                            onClick={() => {
                                dispatch({
                                    type: CHANGE_DATES_START,
                                    payload: {
                                        dispositionStartdate: moment
                                            .utc(tmpStartdate)
                                            .add("1", "day")
                                            .format(),
                                        dispositionEnddate: moment
                                            .utc(tmpEnddate)
                                            .add("1", "day")
                                            .format(),
                                    },
                                });
                                handleClose();
                            }}
                            color="primary"
                            variant={"outlined"}
                            className={classes.submit}
                        >
                            {t("disposition.controls.modal.buttonAcceptLabel")}
                        </Button>
                    </div>
                    <div className={classes.wrapper}>
                        <Button
                            startIcon={<CloseIcon />}
                            onClick={handleClose}
                            color="secondary"
                            size={"small"}
                            variant={"outlined"}
                            className={classes.submit}
                        >
                            {t("disposition.controls.modal.buttonCancelLabel")}
                        </Button>
                    </div>
                </DialogActions>
                <DialogContent>

                    <DateRange
                        className={classes.calendar}
                        months={2}
                        direction="horizontal"
                        onChange={handleDatePickerChange}
                        ranges={[
                            {
                                startDate: moment.utc(tmpStartdate).toDate(),
                                endDate: moment.utc(tmpEnddate).toDate(),
                                key: "selection",
                            },
                        ]}
                        showDateDisplay={false}
                        rangeColors={["#000"]}
                        showMonthAndYearPickers={false}
                        weekStartsOn={1}
                        locale={de}
                    />

                </DialogContent>

            </Dialog>

        </>
        </LocalizationProvider>
    );
};

export default DispositionDateControls;
